/**
 * TOTP enrollment flow (TASK-631): enroll → QR/manual secret → confirm code →
 * backup codes (shown once) → done.
 *
 * Used in two places:
 * - Settings → Security (authenticated user enabling 2FA voluntarily)
 * - /login/2fa challenge page (tenant enforces 2FA, user never enrolled —
 *   authorized by the short-lived challenge token instead of a Bearer token)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Copy, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { httpClient } from '../../lib/httpClient';

interface EnrollData {
  secret: string;
  provisioning_uri: string;
  qr_data_url: string;
}

interface TwoFAEnrollFlowProps {
  /** Present when enrolling mid-login (no access token yet). */
  challengeToken?: string;
  /** Called after the user confirms they saved the backup codes. */
  onEnabled: () => void;
}

const TwoFAEnrollFlow: React.FC<TwoFAEnrollFlowProps> = ({ challengeToken, onEnabled }) => {
  const [enrollData, setEnrollData] = useState<EnrollData | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // POST /enroll rotates the server-side secret — it must fire exactly once.
  // StrictMode's dev double-effect would otherwise race two enrolls and can leave
  // the UI showing a secret the server no longer stores.
  const enrollStarted = useRef(false);

  useEffect(() => {
    // No cleanup-cancellation: under StrictMode the simulated unmount would
    // discard the one allowed response and strand the UI on the spinner.
    if (enrollStarted.current) return;
    enrollStarted.current = true;
    (async () => {
      setIsLoading(true);
      try {
        const data = await httpClient.post<EnrollData>('/api/auth/2fa/enroll', {
          challenge_token: challengeToken ?? null,
        });
        setEnrollData(data);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Could not start 2FA enrollment');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [challengeToken]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (code.trim().length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setIsLoading(true);
    try {
      const data = await httpClient.post<{ enabled: boolean; backup_codes: string[] }>(
        '/api/auth/2fa/enroll/confirm',
        { code: code.trim(), challenge_token: challengeToken ?? null }
      );
      setBackupCodes(data.backup_codes);
      setCode('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Invalid code — try again');
    } finally {
      setIsLoading(false);
    }
  };

  const copyBackupCodes = () => {
    if (backupCodes) {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      toast.success('Backup codes copied');
    }
  };

  // Step 3: backup codes (shown exactly once)
  if (backupCodes) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-green-700">
          <ShieldCheck size={20} />
          <p className="font-medium">Two-factor authentication is enabled</p>
        </div>
        <p className="text-sm text-gray-600">
          Save these backup codes somewhere safe — each works once if you lose your
          authenticator. <span className="font-medium">They will not be shown again.</span>
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-2 gap-1 font-mono text-sm">
          {backupCodes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={copyBackupCodes}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"
          >
            <Copy size={14} /> Copy
          </button>
          <button
            type="button"
            onClick={onEnabled}
            className="px-4 py-2 bg-kielis-navy text-white rounded-lg text-sm hover:bg-opacity-90"
          >
            I saved my backup codes
          </button>
        </div>
      </div>
    );
  }

  if (isLoading && !enrollData) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        <Loader2 size={16} className="animate-spin" /> Preparing enrollment…
      </div>
    );
  }

  if (!enrollData) {
    return <p className="text-sm text-red-600">{error || 'Could not start 2FA enrollment'}</p>;
  }

  // Step 2: QR + manual secret + first code
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Scan this QR code with your authenticator app (Google Authenticator, 1Password, …),
        then enter the 6-digit code it shows.
      </p>
      <div className="flex items-start gap-4">
        <img
          src={enrollData.qr_data_url}
          alt="TOTP enrollment QR code"
          className="w-40 h-40 border border-gray-200 rounded-lg bg-white"
        />
        <div className="text-xs text-gray-500 break-all">
          <p className="mb-1 font-medium text-gray-600">Can't scan? Enter this key manually:</p>
          <code className="bg-gray-50 border border-gray-200 rounded px-2 py-1 font-mono">
            {enrollData.secret}
          </code>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <form onSubmit={handleConfirm} className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="123456"
          className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-kielis-gold"
          disabled={isLoading}
        />
        <button
          type="submit"
          disabled={isLoading || code.length !== 6}
          className="px-4 py-2 bg-kielis-navy text-white rounded-lg text-sm hover:bg-opacity-90 disabled:opacity-50 flex items-center gap-2"
        >
          {isLoading && <Loader2 size={14} className="animate-spin" />}
          Verify
        </button>
      </form>
    </div>
  );
};

export default TwoFAEnrollFlow;
